import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  CheckSquare,
  List,
  LayoutGrid,
  Eye,
  EyeOff,
  AlertTriangle,
  Printer,
} from 'lucide-react';
import { hebrewDateString, getJewishHolidays } from '../utils/hebrewCalendar';
import { getUSFederalHolidays } from '../utils/usHolidays';
import { parseDateLocal } from '../utils/calendarPrint';
import { CalendarPrintModal } from '../components/calendar/CalendarPrintModal';
import { useEvents } from '../hooks/useEvents';
import { useChecklistItemsForCalendar } from '../hooks/useEventOps';
import type { Event } from '@peles/shared';
import type { CalendarTaskItem } from '../api/event-ops';

// ─── Calendar Page ───────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  CONFIRMED: 'bg-green-500',
  PLANNING: 'bg-blue-500',
  DRAFT: 'bg-gray-400',
  IN_PROGRESS: 'bg-purple-500',
  COMPLETED: 'bg-teal-500',
  CANCELLED: 'bg-red-400',
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type ViewMode = 'grid' | 'list';
type ListFilter = 'all' | 'events' | 'tasks';

function getLocalStorageBool(key: string, defaultVal: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return defaultVal;
    return v === 'true';
  } catch {
    return defaultVal;
  }
}

function getLocalStorageStr<T extends string>(key: string, defaultVal: T): T {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return defaultVal;
    return v as T;
  } catch {
    return defaultVal;
  }
}

export function CalendarPage() {
  const navigate = useNavigate();
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const [hideDone, setHideDone] = useState(() => getLocalStorageBool('calendar_hide_done', false));
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    getLocalStorageStr<ViewMode>('calendar_view_mode', 'grid'),
  );
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [showPrintModal, setShowPrintModal] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Persist preferences
  useEffect(() => {
    try {
      localStorage.setItem('calendar_hide_done', String(hideDone));
    } catch {
      // ignore
    }
  }, [hideDone]);

  useEffect(() => {
    try {
      localStorage.setItem('calendar_view_mode', viewMode);
    } catch {
      // ignore
    }
  }, [viewMode]);

  const { data: events = [] } = useEvents();
  const { data: taskData } = useChecklistItemsForCalendar(viewYear, viewMonth);

  const taskItems: CalendarTaskItem[] = taskData?.items ?? [];
  const overdueItems: CalendarTaskItem[] = taskData?.overdueItems ?? [];

  // Filter done tasks if hideDone
  const visibleTaskItems = hideDone ? taskItems.filter((t) => !t.isDone) : taskItems;
  const visibleOverdueItems = hideDone ? overdueItems.filter((t) => !t.isDone) : overdueItems;

  // Build event map keyed by "year-month-day"
  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    events.forEach((ev) => {
      const d = new Date(ev.startAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const list = map.get(key) ?? [];
      list.push(ev);
      map.set(key, list);
      if (ev.endAt) {
        const end = new Date(ev.endAt);
        const cur = new Date(d);
        cur.setDate(cur.getDate() + 1);
        while (cur <= end) {
          const k = `${cur.getFullYear()}-${cur.getMonth()}-${cur.getDate()}`;
          if (!map.has(k)) map.set(k, []);
          const evList = map.get(k)!;
          if (!evList.includes(ev)) evList.push(ev);
          cur.setDate(cur.getDate() + 1);
        }
      }
    });
    return map;
  }, [events]);

  // Build task map keyed by "year-month-day"
  const tasksByDay = useMemo(() => {
    const map = new Map<string, CalendarTaskItem[]>();
    visibleTaskItems.forEach((task) => {
      const d = parseDateLocal(task.dueDate);
      if (!d) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    });
    return map;
  }, [visibleTaskItems]);

  const usHolidays = useMemo(() => getUSFederalHolidays(viewYear), [viewYear]);
  const jewishHolidays = useMemo(
    () => getJewishHolidays(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();
  const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7;

  function prevMonth() {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else setViewMonth((m) => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else setViewMonth((m) => m + 1);
  }

  function goToToday() {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  }

  const cells = Array.from({ length: totalCells }, (_, i) => {
    if (i < firstDayOfMonth) {
      const day = daysInPrevMonth - firstDayOfMonth + i + 1;
      return {
        day,
        month: viewMonth - 1 < 0 ? 11 : viewMonth - 1,
        year: viewMonth - 1 < 0 ? viewYear - 1 : viewYear,
        isCurrentMonth: false,
      };
    } else if (i < firstDayOfMonth + daysInMonth) {
      return {
        day: i - firstDayOfMonth + 1,
        month: viewMonth,
        year: viewYear,
        isCurrentMonth: true,
      };
    } else {
      const day = i - firstDayOfMonth - daysInMonth + 1;
      return {
        day,
        month: viewMonth + 1 > 11 ? 0 : viewMonth + 1,
        year: viewMonth + 1 > 11 ? viewYear + 1 : viewYear,
        isCurrentMonth: false,
      };
    }
  });

  // ─── List view data ────────────────────────────────────────────────────────

  interface ListItem {
    type: 'event' | 'task';
    date: Date;
    key: string;
    event?: Event;
    task?: CalendarTaskItem;
  }

  const showEventsInList = listFilter === 'all' || listFilter === 'events';
  const showTasksInList = listFilter === 'all' || listFilter === 'tasks';

  const listItems = useMemo<ListItem[]>(() => {
    const items: ListItem[] = [];

    if (showEventsInList) {
      events.forEach((ev) => {
        const d = new Date(ev.startAt);
        items.push({ type: 'event', date: d, key: `ev-${ev.id}`, event: ev });
      });
    }

    if (showTasksInList) {
      visibleTaskItems.forEach((task) => {
        const d = parseDateLocal(task.dueDate);
        if (!d) return;
        items.push({ type: 'task', date: d, key: `task-${task.id}`, task });
      });
    }

    items.sort((a, b) => a.date.getTime() - b.date.getTime());
    return items;
  }, [events, visibleTaskItems, showEventsInList, showTasksInList]);

  // Effective view: on mobile always show agenda-style
  const showGrid = !isMobile && viewMode === 'grid';
  const showList = isMobile || viewMode === 'list';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays size={22} className="text-primary-600" />
          <h1 className="text-xl font-bold text-gray-900">Calendar</h1>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-2 flex-wrap">
          {/* Hide done toggle */}
          <button
            onClick={() => setHideDone((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs border rounded-md font-medium transition-colors ${
              hideDone
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title={hideDone ? 'Show done tasks' : 'Hide done tasks'}
          >
            {hideDone ? <EyeOff size={14} /> : <Eye size={14} />}
            <span className="hidden sm:inline">{hideDone ? 'Show Done' : 'Hide Done'}</span>
          </button>

          {/* Grid/List toggle (desktop only) */}
          {!isMobile && (
            <div className="flex rounded-md border border-gray-300 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-2.5 py-1.5 text-xs flex items-center gap-1 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                title="Grid view"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-2.5 py-1.5 text-xs flex items-center gap-1 border-l border-gray-300 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                title="List view"
              >
                <List size={14} />
              </button>
            </div>
          )}

          {/* Print */}
          <button
            onClick={() => setShowPrintModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded-md font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            title="Print calendar"
          >
            <Printer size={14} />
            <span className="hidden sm:inline">Print</span>
          </button>

          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
          >
            Today
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={prevMonth}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Previous month"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-base font-semibold text-gray-800 min-w-[140px] sm:min-w-[160px] text-center">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button
              onClick={nextMonth}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Next month"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Filter pills — always visible, applies to all view modes */}
      <div className="flex gap-2">
        {(['all', 'events', 'tasks'] as ListFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setListFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium border transition-colors ${
              listFilter === f
                ? 'bg-primary-600 text-white border-primary-600'
                : 'text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f === 'all' ? 'All' : f === 'events' ? 'Events' : 'Tasks'}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-100 border border-blue-300 inline-block" />{' '}
          US Holiday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-50 border border-amber-300 inline-block" />{' '}
          Jewish Holiday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" /> Confirmed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Planning
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-purple-500 inline-block" /> In Progress
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-gray-400 inline-block" /> Draft
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-indigo-400 inline-block" /> Task
        </span>
      </div>

      {/* Mobile / List Agenda View */}
      {showList && viewMode !== 'list' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100">
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const cellDate = new Date(viewYear, viewMonth, day);
            const dayKey = `${viewYear}-${viewMonth}-${day}`;
            const dayEvents = showEventsInList ? (eventsByDay.get(dayKey) ?? []) : [];
            const dayTasks = showTasksInList ? (tasksByDay.get(dayKey) ?? []) : [];
            const usHoliday = usHolidays.get(dayKey);
            const jewishList = jewishHolidays.get(dayKey) ?? [];
            const isToday =
              day === today.getDate() &&
              viewMonth === today.getMonth() &&
              viewYear === today.getFullYear();
            const dow = cellDate.getDay();
            const isWeekend = dow === 0 || dow === 6;
            let hebrewLabel = '';
            try {
              hebrewLabel = hebrewDateString(cellDate);
            } catch {
              hebrewLabel = '';
            }
            const hasContent =
              dayEvents.length > 0 || dayTasks.length > 0 || !!usHoliday || jewishList.length > 0;

            return (
              <div
                key={day}
                className={`flex items-stretch gap-3 px-3 py-2 ${
                  isToday
                    ? 'bg-primary-50'
                    : isWeekend
                      ? 'bg-gray-50/60'
                      : usHoliday
                        ? 'bg-blue-50/30'
                        : jewishList.length > 0
                          ? 'bg-amber-50/30'
                          : ''
                }`}
              >
                <div className="flex flex-col items-center justify-center w-12 shrink-0">
                  <span
                    className={`text-[10px] uppercase tracking-wide font-medium ${
                      isToday ? 'text-primary-700' : 'text-gray-500'
                    }`}
                  >
                    {DAY_NAMES[dow]}
                  </span>
                  <span
                    className={`text-xl font-semibold leading-tight ${
                      isToday
                        ? 'text-white bg-primary-600 rounded-full w-8 h-8 flex items-center justify-center'
                        : 'text-gray-800'
                    }`}
                  >
                    {day}
                  </span>
                  {hebrewLabel && (
                    <span
                      className="text-[9px] text-gray-400 mt-0.5 truncate max-w-[52px]"
                      dir="rtl"
                      title={hebrewLabel}
                    >
                      {hebrewLabel.split(' ').slice(0, 2).join(' ')}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1 justify-center py-0.5">
                  {usHoliday && (
                    <div className="text-xs font-medium text-blue-700 truncate" title={usHoliday}>
                      🇺🇸 {usHoliday}
                    </div>
                  )}
                  {jewishList.map((jh, i) => (
                    <div key={i} className="text-xs font-medium text-amber-700 truncate" title={jh}>
                      ✡ {jh}
                    </div>
                  ))}
                  {dayEvents.map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => navigate(`/events/${ev.id}`)}
                      className={`text-left text-xs text-white rounded px-2 py-1 truncate font-medium
                        ${STATUS_COLORS[ev.status] ?? 'bg-gray-400'}
                        hover:opacity-80 transition-opacity`}
                      title={ev.name}
                    >
                      {ev.name}
                    </button>
                  ))}
                  {dayTasks.map((task) => (
                    <button
                      key={task.id}
                      onClick={() =>
                        task.section.checklist.eventId
                          ? navigate(`/events/${task.section.checklist.eventId}/checklist`)
                          : navigate('/tasks')
                      }
                      className={`text-left text-xs text-white rounded px-2 py-1 truncate font-medium bg-indigo-500 hover:opacity-80 transition-opacity flex items-center gap-1 ${task.isDone ? 'opacity-60' : ''}`}
                      title={task.title}
                    >
                      <CheckSquare size={10} className="shrink-0" />
                      {task.title}
                    </button>
                  ))}
                  {!hasContent && <span className="text-xs text-gray-300">—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop Grid View */}
      {showGrid && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden w-full">
          <div className="grid grid-cols-7 border-b border-gray-200">
            {DAY_NAMES.map((d) => (
              <div
                key={d}
                className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {cells.map((cell, idx) => {
              const isToday =
                cell.isCurrentMonth &&
                cell.day === today.getDate() &&
                cell.month === today.getMonth() &&
                cell.year === today.getFullYear();

              const cellDate = new Date(cell.year, cell.month, cell.day);
              const dayKey = `${cell.year}-${cell.month}-${cell.day}`;
              const dayEvents =
                cell.isCurrentMonth && showEventsInList ? (eventsByDay.get(dayKey) ?? []) : [];
              const dayTasks =
                cell.isCurrentMonth && showTasksInList ? (tasksByDay.get(dayKey) ?? []) : [];
              const usHoliday = cell.isCurrentMonth ? usHolidays.get(dayKey) : undefined;
              const jewishList = cell.isCurrentMonth ? (jewishHolidays.get(dayKey) ?? []) : [];

              const hasHoliday = usHoliday || jewishList.length > 0;
              const isSat = cellDate.getDay() === 6;
              const isSun = cellDate.getDay() === 0;

              let hebrewLabel = '';
              if (cell.isCurrentMonth) {
                try {
                  hebrewLabel = hebrewDateString(cellDate);
                } catch {
                  hebrewLabel = '';
                }
              }

              return (
                <div
                  key={idx}
                  className={`min-h-[140px] p-1.5 border-b border-r border-gray-100 flex flex-col gap-0.5
                  ${!cell.isCurrentMonth ? 'bg-gray-50' : hasHoliday ? (usHoliday ? 'bg-blue-50/40' : 'bg-amber-50/40') : ''}
                  ${isSat || isSun ? 'bg-gray-50/60' : ''}
                `}
                >
                  <div className="flex items-start justify-between">
                    <span
                      className={`text-sm font-medium leading-tight rounded-full w-6 h-6 flex items-center justify-center
                      ${isToday ? 'bg-primary-600 text-white' : cell.isCurrentMonth ? 'text-gray-800' : 'text-gray-300'}
                    `}
                    >
                      {cell.day}
                    </span>
                    {hebrewLabel && (
                      <span
                        className="text-[9px] text-gray-400 leading-tight text-right max-w-[60px] truncate"
                        dir="rtl"
                        title={hebrewLabel}
                      >
                        {hebrewLabel}
                      </span>
                    )}
                  </div>

                  {usHoliday && (
                    <div
                      className="text-[10px] font-medium text-blue-600 leading-tight truncate"
                      title={usHoliday}
                    >
                      🇺🇸 {usHoliday}
                    </div>
                  )}

                  {jewishList.slice(0, 2).map((jh, i) => (
                    <div
                      key={i}
                      className="text-[10px] font-medium text-amber-700 leading-tight truncate"
                      title={jh}
                    >
                      ✡ {jh}
                    </div>
                  ))}
                  {jewishList.length > 2 && (
                    <div className="text-[10px] text-amber-500">+{jewishList.length - 2} more</div>
                  )}

                  {dayEvents.slice(0, 2).map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => navigate(`/events/${ev.id}`)}
                      className={`w-full text-left text-[10px] text-white rounded px-1 py-0.5 truncate leading-tight font-medium
                      ${STATUS_COLORS[ev.status] ?? 'bg-gray-400'}
                      hover:opacity-80 transition-opacity`}
                      title={ev.name}
                    >
                      {ev.name}
                    </button>
                  ))}
                  {dayEvents.length > 2 && (
                    <span className="text-[10px] text-gray-500 pl-0.5">
                      +{dayEvents.length - 2} more
                    </span>
                  )}

                  {dayTasks.slice(0, 2).map((task) => (
                    <button
                      key={task.id}
                      onClick={() =>
                        task.section.checklist.eventId
                          ? navigate(`/events/${task.section.checklist.eventId}/checklist`)
                          : navigate('/tasks')
                      }
                      className={`w-full text-left text-[10px] text-white rounded px-1 py-0.5 truncate leading-tight font-medium bg-indigo-400 hover:opacity-80 transition-opacity flex items-center gap-0.5 ${task.isDone ? 'opacity-50' : ''}`}
                      title={`${task.title} (${task.section.checklist.title ?? 'Task List'})`}
                    >
                      <CheckSquare size={8} className="shrink-0" />
                      {task.title}
                    </button>
                  ))}
                  {dayTasks.length > 2 && (
                    <span className="text-[10px] text-indigo-500 pl-0.5">
                      +{dayTasks.length - 2} tasks
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Desktop List View */}
      {!isMobile && viewMode === 'list' && (
        <div className="space-y-3">
          {listItems.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 text-sm">
              Nothing to show
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden divide-y divide-gray-100">
              {listItems.map((item) => {
                const dateLabel = item.date.toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                });
                if (item.type === 'event' && item.event) {
                  const ev = item.event;
                  return (
                    <div key={item.key} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-24 text-xs text-gray-500 shrink-0">{dateLabel}</div>
                      <div
                        className={`w-2 h-2 rounded-full shrink-0 ${STATUS_COLORS[ev.status] ?? 'bg-gray-400'}`}
                      />
                      <button
                        onClick={() => navigate(`/events/${ev.id}`)}
                        className="flex-1 text-left text-sm font-medium text-gray-900 hover:text-primary-600 truncate"
                      >
                        {ev.name}
                      </button>
                      <span className="text-xs text-gray-400 shrink-0">{ev.status}</span>
                    </div>
                  );
                }
                if (item.type === 'task' && item.task) {
                  const task = item.task;
                  return (
                    <div key={item.key} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-24 text-xs text-gray-500 shrink-0">{dateLabel}</div>
                      <CheckSquare
                        size={14}
                        className={`shrink-0 ${task.isDone ? 'text-green-500' : 'text-indigo-400'}`}
                      />
                      <button
                        onClick={() =>
                          task.section.checklist.eventId
                            ? navigate(`/events/${task.section.checklist.eventId}/checklist`)
                            : navigate('/tasks')
                        }
                        className={`flex-1 text-left text-sm font-medium hover:text-primary-600 truncate ${task.isDone ? 'line-through text-gray-400' : 'text-gray-900'}`}
                      >
                        {task.title}
                      </button>
                      <span className="text-xs text-gray-400 shrink-0 truncate max-w-[120px]">
                        {task.section.checklist.title ?? 'Task List'}
                      </span>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      )}

      {/* Overdue Tasks Section */}
      {visibleOverdueItems.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-600" />
            <h2 className="text-sm font-semibold text-red-700">
              Overdue Tasks ({visibleOverdueItems.length})
            </h2>
          </div>
          <div className="space-y-1.5">
            {visibleOverdueItems.map((task) => {
              const d = parseDateLocal(task.dueDate);
              const dateLabel = d
                ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '';
              return (
                <div key={task.id} className="flex items-center gap-2">
                  <CheckSquare size={13} className="text-red-400 shrink-0" />
                  <button
                    onClick={() =>
                      task.section.checklist.eventId
                        ? navigate(`/events/${task.section.checklist.eventId}/checklist`)
                        : navigate('/tasks')
                    }
                    className="flex-1 text-left text-sm text-red-800 hover:text-red-600 truncate"
                  >
                    {task.title}
                  </button>
                  {dateLabel && <span className="text-xs text-red-500 shrink-0">{dateLabel}</span>}
                  <span className="text-xs text-red-400 shrink-0 truncate max-w-[100px]">
                    {task.section.checklist.title ?? 'Task List'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && (
        <CalendarPrintModal
          initialYear={viewYear}
          initialMonth={viewMonth}
          initialHideDone={hideDone}
          onClose={() => setShowPrintModal(false)}
        />
      )}
    </div>
  );
}
